import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BetsButton from '../Vets/Button';
import { Container, ModalContainer } from '../BaseModal';
import { formatUSD, formatVets } from '../../common/currency';
import { useLazyGetSwapQuoteQuery } from '../../redux/api/authorized/swap';
import { selectPrivyToken } from '../../redux/slices/auth';
import { useSolana, toTokenAmount } from '../../hooks/useSolana';
import { selectTokenBySymbol } from '../../redux/slices/tokenSlice';
import BackButton from '../BackButton';
import { toast } from 'react-toastify';

const ButtonsContainer = styled.div`
  margin-top: 20px;
  width: 100%;
  display: flex;
  gap: 20px;
`;

const Title = styled.h1`
  color: ${({ theme }) => theme.text.title};
  font-size: 40px;
  font-weight: 600;
  font-family: Manrope;
`;

const YourBalanceLabel = styled.p`
  color: ${({ theme }) => theme.text.regular};
  font-size: 16px;
  font-weight: 400;
  font-family: Manrope;
  margin-top: 10px;
  margin-bottom: -5px;
`;

const VetsBalance = styled.p`
  color: ${({ theme }) => theme.text.regular};
  font-size: 36px;
  font-weight: 600;
  font-family: Manrope;
`;

const VetsTokenBalance = styled.p`
  color: ${({ theme }) => theme.text.regular};
  font-size: 12px;
  font-weight: 400;
  font-family: Manrope;
  margin-bottom: 10px;
`;

const TwoSidesContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ConfirmBuyVets = () => {
  const privyToken = useSelector(selectPrivyToken);
  const [vetsPrice, setVetsPrice] = useState(null);
  const [tokensToReceive, setTokensToReceive] = useState(0);
  const [triggerGetSwapQuote] = useLazyGetSwapQuoteQuery();
  const { executeSwap, loading } = useSolana();
  const dispatch = useDispatch();

  const [searchParams] = useSearchParams();

  const usdAmount = searchParams.get('usdAmount');

  const vetsToken = useSelector((state) =>
    selectTokenBySymbol(state, 'solana', 'VETS'),
  );
  const vetsTokenAddress = vetsToken?.address;

  useEffect(() => {
    const fetchQuote = async () => {
      const resp = await triggerGetSwapQuote({
        inputMint: import.meta.env.VITE_SOLANA_USDC_MINT,
        outputMint: vetsTokenAddress,
        amount: toTokenAmount(usdAmount, 6),
      }).unwrap();
      setTokensToReceive(formatVets(resp.quote.outAmount));
    };

    if (privyToken && vetsTokenAddress) {
      fetchQuote();
    }
  }, [triggerGetSwapQuote, privyToken, vetsTokenAddress]);

  useEffect(() => {
    const fetchQuote = async () => {
      const resp = await triggerGetSwapQuote({
        inputMint: vetsTokenAddress,
        outputMint: import.meta.env.VITE_SOLANA_USDC_MINT,
        amount: toTokenAmount(1, 9),
      }).unwrap();
      const price = parseInt(resp.quote.outAmount) / 1000000;
      setVetsPrice(price);
    };

    if (privyToken && vetsTokenAddress) {
      fetchQuote();
    }
  }, [triggerGetSwapQuote, privyToken, vetsTokenAddress]);

  const navigate = useNavigate();

  const handleSwap = async () => {
    const usdcMint = import.meta.env.VITE_SOLANA_USDC_MINT;
    const vetsMint = vetsTokenAddress;
    const resp = await executeSwap({
      inputMint: usdcMint,
      outputMint: vetsMint,
      amount: toTokenAmount(usdAmount, 6),
    });
    if (resp) {
      toast.success('Purchase Completed Successfully');
      navigate('/app/wallet');
    } else {
      toast.error('Error Processing Purchase');
    }
  };

  return (
    <Container>
      <ModalContainer>
        <BackButton />
        <div>
          <Title>Buy VETS</Title>
        </div>
        <div>
          <YourBalanceLabel>Total Cost</YourBalanceLabel>
          <VetsBalance>{formatUSD(usdAmount)}</VetsBalance>
          <VetsTokenBalance>
            VETS price: {formatUSD(vetsPrice)}
          </VetsTokenBalance>
        </div>
        <div>
          <TwoSidesContainer>
            <VetsTokenBalance>VETS Amount</VetsTokenBalance>
            <VetsTokenBalance>{tokensToReceive} VETS</VetsTokenBalance>
          </TwoSidesContainer>
          <TwoSidesContainer>
            <VetsTokenBalance>Buy Amount</VetsTokenBalance>
            <VetsTokenBalance>
              {formatUSD(Number(usdAmount) * 0.96)}
            </VetsTokenBalance>
          </TwoSidesContainer>
          <TwoSidesContainer>
            <VetsTokenBalance>Cause Fee (4%)</VetsTokenBalance>
            <VetsTokenBalance>
              {formatUSD(Number(usdAmount) * 0.04)}
            </VetsTokenBalance>
          </TwoSidesContainer>
          <TwoSidesContainer>
            <VetsTokenBalance>Total Cost</VetsTokenBalance>
            <VetsTokenBalance>{formatUSD(usdAmount)}</VetsTokenBalance>
          </TwoSidesContainer>
        </div>
        <ButtonsContainer>
          <BetsButton
            disabled={!vetsTokenAddress}
            loading={loading}
            fullWidth
            onClick={() => {
              handleSwap();
            }}
          >
            Confirm
          </BetsButton>
        </ButtonsContainer>
      </ModalContainer>
    </Container>
  );
};

export default ConfirmBuyVets;
